import { DateTime } from 'luxon';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';

import { resendVerificationRetry } from 'app-components/flux/credit-profile/actions';
import { getLastRetryDate } from 'app-components/flux/credit-profile/selectors';
import { rootUiActions } from 'app-components/flux/root-ui';

import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';

import { triggerCancelRegistrationDialog } from './actions';
import styles from './styles';
import { getLast4Digits } from './utils';

interface IVerificationSentDialogProps {
  isVerificationSuccess: boolean;
  creditProfileData: Record<string, any>;
  flowId?: string;
}

const MAX_TIMEOUT = 60; // 60 seconds

const useStyles = makeStyles()(styles);

const CreditScoreVerificationSentDialog: React.FC<IVerificationSentDialogProps> = (props) => {
  const { isVerificationSuccess, creditProfileData, flowId } = props;

  const { classes } = useStyles();
  const timerInterval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const [timer, setTimer] = useState(MAX_TIMEOUT);
  const lastRetryTime = useSelector(getLastRetryDate);
  const dispatch = useDispatch();

  useEffect(() => {
    if (lastRetryTime) {
      const currentTime = DateTime.now();
      const lastAttemptTime = DateTime.fromISO(lastRetryTime);
      const { seconds } = currentTime.diff(lastAttemptTime, ['seconds']);
      const remainingTime = MAX_TIMEOUT - Math.trunc(seconds);
      const updatedTime = remainingTime > 0 ? remainingTime : 0;
      setTimer(() => updatedTime);
    }

    timerInterval.current = setInterval(() => {
      setTimer((timer) => timer - 1);
    }, 1000);

    return () => clearInterval(timerInterval.current);
  }, [lastRetryTime]);

  useEffect(() => {
    if (timer === 0) {
      clearInterval(timerInterval.current);
    }
  }, [timer]);

  const handleResendVerficationLink = () => {
    dispatch(
      resendVerificationRetry(
        {
          creditProfileRegistrationId: creditProfileData.id,
        },
        { props: { flowId } },
      ),
    );
  };

  const handleBackOnRegistrationFailed = () => {
    dispatch(rootUiActions.removeDialog());
  };

  const handleDialogClose = () => {
    if (isVerificationSuccess) {
      dispatch(triggerCancelRegistrationDialog(flowId));
    } else {
      handleBackOnRegistrationFailed();
    }
  };

  return (
    <StdDialog
      open
      showCloseButton
      size='xs'
      disableBackdropClick
      title={isVerificationSuccess ? 'Verification Link Sent' : 'Verification Failed'}
      onClose={handleDialogClose}
    >
      <DialogContent>
        {isVerificationSuccess ? (
          <>
            <Typography variant='captionRegular' fontWeight={500}>
              Verification Link Sent
            </Typography>
            <Box bgcolor='info.light' p={1} my={2}>
              <Typography variant='captionRegular'>We’ve sent a link to your mobile number</Typography>
              <Typography variant='titleMedium'>
                (xxx) - xxx - {getLast4Digits(creditProfileData.creditProfile.phoneNumber)}
              </Typography>
              <Typography variant='captionRegular'>
                Tap the link in the text message to verify your identity.
              </Typography>
            </Box>
            <Typography variant='captionRegular' color='text.secondary' fontSize={10}>
              The link will expire in four minutes.
              <br />
              Once the phone number is verified, you will be directed to your credit score.
              <br />
              If you have not received a message with the verification link,
              <br />
              <Button
                variant='text'
                disabled={timer !== 0}
                onClick={handleResendVerficationLink}
                sx={{
                  textTransform: 'none',
                  fontSize: 10,
                  padding: 0,
                }}
                size='small'
                id='credit-score-resend-link-btn'
              >
                resend the verification link
              </Button>
              {timer > 0 && ` (in ${timer}s)`}
            </Typography>
          </>
        ) : (
          <Typography variant='paragraphRegular'>
            We could not verify your identity using the information you provided. Please check your phone number and
            other information, then try again.
          </Typography>
        )}
      </DialogContent>
      {!isVerificationSuccess && (
        <DialogActions>
          <QButton
            id='credit-score-verification-failed-back-btn'
            variant='contained'
            onClick={handleBackOnRegistrationFailed}
            className={classes.btnDialogAction}
          >
            Back
          </QButton>
        </DialogActions>
      )}
    </StdDialog>
  );
};

export default CreditScoreVerificationSentDialog;
